import React, { useEffect, useMemo, useState } from "react";
import { Table, Skeleton } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getBookings } from "../features/booking/bookingSlice";
import "../styles/Dashboard.css";

const Dashboard = () => {
  const dispatch = useDispatch();
  const [selectedType, setSelectedType] = useState("all");

  useEffect(() => {
    dispatch(getBookings());
  }, [dispatch]);

  const { bookings = [], isLoading } = useSelector((state) => state.booking);

  const filteredBookings = useMemo(() => {
    if (selectedType === "all") return bookings;

    return bookings.filter((booking) => booking.serviceType === selectedType);
  }, [bookings, selectedType]);

  const stats = useMemo(() => {
    const completedOrders = filteredBookings.filter(
      (booking) => booking.status === "Completed"
    );

    const completedRevenue = completedOrders.reduce((total, booking) => {
      return total + Number(booking.price || 0);
    }, 0);

    return {
      totalOrders: filteredBookings.length,
      newOrders: filteredBookings.filter((b) => b.status === "New").length,
      progressOrders: filteredBookings.filter(
        (b) => b.status === "Submitted" || b.status === "Contacted"
      ).length,
      completedOrders: completedOrders.length,
      cancelledOrders: filteredBookings.filter((b) => b.status === "Cancelled")
        .length,
      completedRevenue,
    };
  }, [filteredBookings]);

const recentOrders = useMemo(() => {
  return [...filteredBookings].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
}, [filteredBookings]);

  const columns = [
    {
      title: "Customer",
      dataIndex: "name",
      width: 180,
    },
    {
      title: "Service",
      dataIndex: "serviceType",
      width: 140,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      width: 150,
    },
    {
      title: "Date",
      dataIndex: "preferredDate",
      width: 130,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 130,
    },
    {
      title: "Amount",
      dataIndex: "price",
      width: 130,
    },
  ];

  const tableData = recentOrders.map((booking, index) => ({
    key: booking._id || index,
    name: booking.name || "N/A",
    serviceType: booking.serviceType || "N/A",
    phone: booking.phone || "N/A",
    preferredDate: booking.preferredDate
      ? new Date(booking.preferredDate).toLocaleDateString()
      : "N/A",
    status: <span className={`dash-status ${booking.status}`}>{booking.status}</span>,
    price: `KSh ${Number(booking.price || 0).toLocaleString()}`,
  }));

  return (
    <main className="dashboard-page">
      <div className="dashboard-top">
        <div>
          <h2>Dashboard</h2>
          <p>Track bookings, revenue, and service performance.</p>
        </div>

        <select
          className="dashboard-filter"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
        >
          <option value="all">All Services</option>
          <option value="carwash">Car Wash</option>
          <option value="carpet">Carpet Cleaning</option>
          <option value="water">Water Refill</option>
          <option value="water-refill">Water Refill Old</option>
        </select>
      </div>

      {isLoading && bookings.length === 0 ? (
        <div className="dashboard-skeleton">
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      ) : (
        <>
          <section className="dashboard-revenue-card">
            <div>
              <span>Completed Orders Revenue</span>
              <h1>KSh {stats.completedRevenue.toLocaleString()}</h1>
              <p>Total amount from completed orders only.</p>
            </div>

            <div className="revenue-circle">
              <span>{stats.completedOrders}</span>
              <small>Completed</small>
            </div>
          </section>

          <section className="dashboard-cards">
            <div className="dashboard-card">
              <span>Total Orders</span>
              <h3>{stats.totalOrders}</h3>
            </div>

            <div className="dashboard-card">
              <span>New Orders</span>
              <h3>{stats.newOrders}</h3>
            </div>

            <div className="dashboard-card">
              <span>Progress Orders</span>
              <h3>{stats.progressOrders}</h3>
            </div>

            <div className="dashboard-card">
              <span>Completed Orders</span>
              <h3>{stats.completedOrders}</h3>
            </div>

            <div className="dashboard-card">
              <span>Cancelled Orders</span>
              <h3>{stats.cancelledOrders}</h3>
            </div>
          </section>

          <section className="dashboard-insights">
            <div className="insight-card">
              <h4>Service Breakdown</h4>

              <div className="insight-row">
                <span>Car Wash</span>
                <strong>
                  {bookings.filter((b) => b.serviceType === "carwash").length}
                </strong>
              </div>

              <div className="insight-row">
                <span>Carpet Cleaning</span>
                <strong>
                  {bookings.filter((b) => b.serviceType === "carpet").length}
                </strong>
              </div>

              <div className="insight-row">
                <span>Water Refill</span>
                <strong>
                  {
                    bookings.filter(
                      (b) =>
                        b.serviceType === "water" ||
                        b.serviceType === "water-refill"
                    ).length
                  }
                </strong>
              </div>
            </div>

            <div className="insight-card">
              <h4>Quick Summary</h4>

              <p>
                You currently have <strong>{stats.newOrders}</strong> new orders
                waiting for action and <strong>{stats.progressOrders}</strong>{" "}
                orders in progress.
              </p>

              <p>
                Completed revenue is currently{" "}
                <strong>KSh {stats.completedRevenue.toLocaleString()}</strong>.
              </p>
            </div>
          </section>

          <section className="dashboard-table-card">
            <div className="table-header">
              <h3>Recent Orders</h3>
              <span>Latest 8 bookings</span>
            </div>

           <Table
            columns={columns}
            dataSource={tableData}
            pagination={{
                pageSize: 8,
                showSizeChanger: true,
                pageSizeOptions: ["8", "15", "25", "50"],
            }}
            scroll={{ x: 950 }}
            />
          </section>
        </>
      )}
    </main>
  );
};

export default Dashboard;